import { opt } from '../lib/media';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, User, Menu, X, Heart } from 'lucide-react';
import { categories, LOGO_URL } from '../mock';
import { useCart } from '../context/CartContext';

const topNav = [
  { label: 'New Arrivals', to: '/shop' },
  { label: 'Indo-Western Dresses', to: '/shop/indo-western-dresses' },
  { label: 'Gowns', to: '/shop/gowns' },
  { label: 'Sarees', to: '/shop/indo-western-sarees' },
  { label: 'Ready To Wear', to: '/shop/ready-to-wear' },
  { label: 'Happy Clients', to: '/happy-clients' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState('');
  const { cartCount, wishlist } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    setSearchOpen(false);
    navigate(`/shop?q=${encodeURIComponent(q)}`);
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Announcement bar */}
      <div className="bg-burgundy-950 text-cream text-center text-[11px] md:text-xs tracking-wideluxe py-2 px-4">
        FREE SHIPPING ACROSS INDIA · CUSTOM-MADE COUTURE · DELIVERED IN 2–3 WEEKS
      </div>

      <div className={`transition-all duration-500 border-b ${scrolled ? 'bg-cream/95 backdrop-blur border-gold/20 shadow-sm' : 'bg-cream border-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Left icons (mobile menu) */}
            <div className="flex items-center gap-4 flex-1">
              <button className="lg:hidden text-burgundy-900" onClick={() => setMobileOpen(true)} aria-label="Menu">
                <Menu size={24} />
              </button>
              <button onClick={() => setSearchOpen((s) => !s)} className="hidden lg:flex items-center gap-2 text-burgundy-900 hover:text-gold-dark transition-colors" aria-label="Search">
                <Search size={19} />
                <span className="text-xs tracking-wideluxe uppercase">Search</span>
              </button>
            </div>

            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex flex-col items-center">
              <img src={opt(LOGO_URL, 200)} alt="HITÉRA" className="h-14 md:h-16 w-auto object-contain rounded-sm" />
            </Link>

            {/* Right icons */}
            <div className="flex items-center gap-4 md:gap-6 flex-1 justify-end text-burgundy-900">
              <button onClick={() => setSearchOpen((s) => !s)} className="lg:hidden" aria-label="Search"><Search size={21} /></button>
              <Link to="/cart" className="hidden md:block hover:text-gold-dark transition-colors relative" aria-label="Wishlist">
                <Heart size={20} />
                {wishlist.length > 0 && <span className="absolute -top-2 -right-2 bg-gold text-burgundy-950 text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{wishlist.length}</span>}
              </Link>
              <button className="hidden md:block hover:text-gold-dark transition-colors" aria-label="Account"><User size={20} /></button>
              <Link to="/cart" className="relative hover:text-gold-dark transition-colors" aria-label="Cart">
                <ShoppingBag size={21} />
                {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-gold text-burgundy-950 text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-medium">{cartCount}</span>}
              </Link>
            </div>
          </div>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center justify-center gap-9 pb-4">
            {topNav.map((n) => (
              <Link key={n.label} to={n.to} className="nav-underline text-[12px] tracking-wideluxe uppercase text-burgundy-900 hover:text-gold-dark transition-colors">
                {n.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Search dropdown */}
        {searchOpen && (
          <div className="border-t border-gold/20 bg-cream">
            <form onSubmit={submitSearch} className="max-w-[1400px] mx-auto px-6 py-5 flex items-center gap-4">
              <Search size={20} className="text-gold-dark" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for gowns, sarees, dresses..." className="flex-1 bg-transparent outline-none text-burgundy-900 placeholder:text-burgundy-900/40 tracking-wide" />
              <button type="button" onClick={() => setSearchOpen(false)}><X size={20} className="text-burgundy-900" /></button>
            </form>
          </div>
        )}
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[82%] max-w-sm bg-cream flex flex-col fade-up">
            <div className="flex items-center justify-between p-5 border-b border-gold/20">
              <img src={opt(LOGO_URL, 200)} alt="HITÉRA" className="h-12 rounded-sm" />
              <button onClick={() => setMobileOpen(false)}><X size={24} className="text-burgundy-900" /></button>
            </div>
            <div className="flex flex-col p-5 gap-1 overflow-y-auto">
              {topNav.map((n) => (
                <Link key={n.label} to={n.to} onClick={() => setMobileOpen(false)} className="py-3 border-b border-gold/10 text-sm tracking-wideluxe uppercase text-burgundy-900">
                  {n.label}
                </Link>
              ))}
              <Link to="/about" onClick={() => setMobileOpen(false)} className="py-3 border-b border-gold/10 text-sm tracking-wideluxe uppercase text-burgundy-900">Our Story</Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="py-3 border-b border-gold/10 text-sm tracking-wideluxe uppercase text-burgundy-900">Contact</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
